import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { getUnixTime, isAfter } from 'date-fns';
import type { AuthState } from '../../model/auth.types';
import { authActions, initialState } from '../../model/auth.slice';
import {
    extractAuthDataFromAuthResponse,
    startRefreshCycle,
    clearRefreshCycle,
} from '../services/authService';
import { postLogin, postRefresh } from '../../api/authentication.api';

// TODO: This is a placeholder Store type - apps will need to extend this
// The actual Store type will be provided by each app's store
export type Store = {
    authSlice: AuthState;
};

export type AuthConfig = {
    isB2B?: boolean; // If true, uses 'b2b' as username field in login, otherwise 'liftOperator'
    localStorageKey: string;
    getFromLocalStorage: <T>(key: string, defaultValue: T) => T;
    saveToLocalStorage: (key: string, value: unknown) => void;
    deleteFromLocalStorage: (key: string) => void;
    isOfTypeBackendError?: (error: unknown) => boolean;
    isOfTypeString?: (value: unknown) => value is string;
    extractAuthOptions?: {
        tokenExpiresAtOffset?: number; // Direct offset in seconds (e.g., validFor - 5)
        tokenExpiresAtMultiplier?: number; // Multiplier for validFor (e.g., 0.5 for validFor / 2)
        usePermissionsFromJwt?: boolean;
    };
    refreshTimeLeftMultiplier?: number; // Multiplier for refresh time (e.g., 0.75 for 75% of validFor)
    enableUrlAuth?: boolean; // Enable URL-based authentication (for admin/b2b)
};

export const useAuthentication = (config?: AuthConfig) => {
    const authStore =
        useSelector((store: Store) => store.authSlice) ?? initialState;
    const dispatch = useDispatch();

    const setAuthData = useCallback(
        (data: {
            jwt: string;
            refreshCode: string;
            validFor: number;
            tokenExpiresAt: number;
            permissions: import('../../model/auth.types').Permission[];
            userType: import('../../model/auth.types').UserType;
            userId: string;
            liftOperatorData: {
                skiOperator: string;
                liftOperatorId: string;
            };
        }) => {
            dispatch(authActions.setAuthData(data));
        },
        [dispatch]
    );

    const clearAuthData = useCallback(
        (options?: { userLogout?: boolean }) => {
            dispatch(authActions.clearAuthData(options || {}));
        },
        [dispatch]
    );

    // Always define hooks, but conditionally use config
    const login = useCallback(
        async (
            username: string,
            password: string
        ): Promise<
            [
                data: AuthState | null,
                error: 'bad_credentials' | 'general' | null,
            ]
        > => {
            if (!config) {
                throw new Error(
                    'useAuthentication: config is required for login'
                );
            }

            const {
                isB2B = false,
                localStorageKey,
                saveToLocalStorage,
                extractAuthOptions,
                refreshTimeLeftMultiplier,
                isOfTypeBackendError = () => false,
            } = config;

            try {
                const data = await postLogin(username, password, { isB2B });
                const storeData = extractAuthDataFromAuthResponse(
                    data,
                    username,
                    extractAuthOptions
                );

                saveToLocalStorage(localStorageKey, storeData);
                setAuthData(storeData);

                const timeLeft =
                    extractAuthOptions?.tokenExpiresAtOffset !== undefined
                        ? Math.max(0, (storeData.validFor - 180) * 1000)
                        : storeData.validFor *
                          (refreshTimeLeftMultiplier ?? 0.75) *
                          1000;

                startRefreshCycle({
                    refreshCode: storeData.refreshCode,
                    username,
                    timeLeft,
                    onRefresh: postRefresh,
                    onSuccess: (data) => {
                        setAuthData(data);
                        config.saveToLocalStorage(config.localStorageKey, data);
                    },
                    onError: () => {
                        clearAuthData({ userLogout: false });
                    },
                });

                return [storeData, null];
            } catch (e) {
                console.error(e);

                if (isOfTypeBackendError(e)) {
                    if ((e as { status?: number }).status === 401) {
                        return [null, 'bad_credentials'];
                    }
                }

                return [null, 'general'];
            }
        },
        [config, setAuthData, clearAuthData]
    );

    const logout = useCallback(() => {
        if (!config) {
            throw new Error('useAuthentication: config is required for logout');
        }

        clearRefreshCycle();
        config.deleteFromLocalStorage(config.localStorageKey);
        clearAuthData({
            userLogout: true,
        });
    }, [config, clearAuthData]);

    const isLoggedIn = useCallback((): [
        isLoggedIn: boolean,
        error: 'no_jwt' | 'token_expired' | null,
    ] => {
        const { tokenExpiresAt, jwt } = authStore;

        if (!jwt || !tokenExpiresAt) {
            return [false, 'no_jwt'];
        }

        if (isAfter(new Date(), new Date(tokenExpiresAt * 1000))) {
            return [false, 'token_expired'];
        }

        return [true, null];
    }, [authStore]);

    const saveAuthDataFromUrl = useCallback((): boolean => {
        if (!config || !config.enableUrlAuth) {
            return false;
        }

        const {
            isOfTypeString = (v): v is string => typeof v === 'string',
            extractAuthOptions,
            localStorageKey,
            deleteFromLocalStorage,
            saveToLocalStorage,
        } = config;

        const searchParams = new URLSearchParams(window.location.search);

        const jwt = searchParams.get('jwt');
        const refreshCode = searchParams.get('refreshCode');
        const validFor = searchParams.get('validFor');
        const liftOperatorName = searchParams.get('liftOperatorName');

        if (!jwt || !refreshCode || !validFor) {
            return false;
        }

        if (
            isOfTypeString(jwt) &&
            isOfTypeString(refreshCode) &&
            isOfTypeString(validFor) &&
            isOfTypeString(liftOperatorName)
        ) {
            const validForParsed = parseInt(validFor, 10);

            if (Number.isNaN(validForParsed)) {
                return false;
            }

            const storeData = extractAuthDataFromAuthResponse(
                {
                    jwt,
                    refreshCode,
                    validFor: validForParsed,
                    longLivedToken: '',
                },
                liftOperatorName,
                extractAuthOptions
            );

            deleteFromLocalStorage(localStorageKey);
            saveToLocalStorage(localStorageKey, storeData);
            setAuthData(storeData);

            return true;
        }

        return false;
    }, [config, setAuthData]);

    const authenticateFromLocalStorageData = useCallback((): boolean => {
        if (!config) {
            return false;
        }

        const {
            localStorageKey,
            getFromLocalStorage,
            extractAuthOptions,
            refreshTimeLeftMultiplier,
            saveToLocalStorage,
        } = config;

        const authState = getFromLocalStorage(
            localStorageKey,
            initialState
        ) as AuthState;

        if (!!authState.tokenExpiresAt && !!authState.jwt) {
            if (
                isAfter(new Date(), new Date(authState.tokenExpiresAt * 1000))
            ) {
                setAuthData(initialState);
                return false;
            }

            const timeLeft = authState.tokenExpiresAt - getUnixTime(new Date());
            const refreshTime = extractAuthOptions?.tokenExpiresAtOffset
                ? Math.max(0, (timeLeft - 180) * 1000)
                : timeLeft * refreshTimeLeftMultiplier * 1000;

            startRefreshCycle({
                refreshCode: authState.refreshCode,
                username: authState.liftOperatorData.skiOperator,
                timeLeft: refreshTime,
                onRefresh: postRefresh,
                onSuccess: (data) => {
                    setAuthData(data);
                    saveToLocalStorage(localStorageKey, data);
                },
                onError: () => {
                    clearAuthData({ userLogout: false });
                },
            });

            setAuthData(authState);
            return true;
        }

        return false;
    }, [config, setAuthData, clearAuthData]);

    // If no config provided, return basic functionality
    if (!config) {
        return {
            ...authStore,
            setAuthData,
            clearAuthData,
        };
    }

    return {
        ...authStore,
        setAuthData,
        clearAuthData,
        login,
        logout,
        isLoggedIn,
        saveAuthDataFromUrl,
        authenticateFromLocalStorageData,
    };
};
